import { BsdsInput, BsdsInputPasswordToggle } from '@bsds-lib-component/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { UserkeyInputProps } from '../../types/formType';
import { validateUserkey } from '../../utils/userkeyCreationUtils';

export const UserkeyInput: React.FC<UserkeyInputProps> = ({
  value,
  onChange,
  onValidationChange,
  placeholder = "Digite sua senha",
  required = false,
  disabled = false,
  className = "",
  error,
  label,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  'aria-disabled': ariaDisabled,
  'aria-autocomplete': ariaAutocomplete,
  'aria-haspopup': ariaHasPopup,
  'aria-controls': ariaControls,
  'aria-owns': ariaOwns,
  'aria-activedescendant': ariaActiveDescendant,
  'aria-errormessage': ariaErrorMessage,
}) => {
  const [hasBeenTouched, setHasBeenTouched] = useState<boolean>(false);
  const [showUserkey, setShowUserkey] = useState<boolean>(false);
  const lastValidationRef = useRef<boolean | null>(null);

  // Handle input change (onInput event - real time)
  const handleInput = useCallback((event: React.FormEvent<HTMLBsdsInputElement>) => {
    // Fix: Properly extract value from event
    let newValue = '';
    const target = event.target as HTMLInputElement;

    if (target?.value !== undefined) {
      newValue = target.value;
    }

    // Ensure we always pass a string
    const stringValue = typeof newValue === 'string' ? newValue : String(newValue || '');

    onChange(stringValue);
    setHasBeenTouched(true);
  }, [onChange]);

  // Handle userkey visibility toggle
  const handleToggleUserkey = useCallback(() => {
    setShowUserkey(prev => !prev);
  }, []);

  // Validate userkey when value changes
  useEffect(() => {
    let valid: boolean;
    if (value.length === 0) {
      // Empty value - valid if not required, invalid if required
      valid = !required;
    } else {
      // Validate userkey against all rules
      const validationResult = validateUserkey(value);
      valid = validationResult.isValid;
    }

    if (lastValidationRef.current !== valid) {
      lastValidationRef.current = valid;
      onValidationChange?.(valid);
    }
  }, [value, onValidationChange, required]);

  // Determine error message
  const getErrorMessage = () => {
    if (error) return error;

    if (hasBeenTouched && required && value.length === 0) {
      return 'Senha é obrigatória';
    }

    return '';
  };

  const errorMessage = getErrorMessage();

  return (
    <BsdsInput
      value={value}
      onInput={handleInput}
      label={label}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      className={className}
      clearInput={false}
      type={showUserkey ? "text" : "password"}
      errorText={errorMessage}
      platform="web"
      maxlength={16}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      aria-required={ariaRequired}
      aria-disabled={ariaDisabled}
      aria-autocomplete={ariaAutocomplete}
      aria-haspopup={ariaHasPopup}
      aria-controls={ariaControls}
      aria-owns={ariaOwns}
      aria-activedescendant={ariaActiveDescendant}
      aria-errormessage={ariaErrorMessage}
    >
      <BsdsInputPasswordToggle
        slot="end"
        onClick={handleToggleUserkey}
      />
    </BsdsInput>
  );
};

